/**
 * Care-time summary hook.
 *
 * Wraps `computeCoCMEligibility` for the patient-profile widget and
 * the CoCM registry rows. Subscribes to care-time domain events so
 * any write (create/update/delete) refreshes the summary for the
 * relevant patient.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { Patient } from "@medplum/fhirtypes";
import { computeCoCMEligibility } from "@benchmark/core/services";
import {
  DomainEventNames,
  globalEventEmitter,
  type CareTimeCreatedEvent,
  type CareTimeDeletedEvent,
  type CareTimeUpdatedEvent,
} from "@benchmark/core/events";
import type { CareTimeSummary } from "@benchmark/core/types";
import { getCalendarMonthBounds } from "@/utils/ui/care-time-dates";
type EventWithPatient =
  CareTimeCreatedEvent | CareTimeUpdatedEvent | CareTimeDeletedEvent;

/** Options for the care-time summary hook. @agentic connections */
export type UseCareTimeSummaryOptions = {
  /** Month to compute summary for; defaults to current calendar month. */
  month?: Date;
  /**
   * Patient resource when already loaded — reconciles patient-level CoCM
   * consent with the CarePlan enrollment consent without an extra fetch.
   */
  patient?: Patient;
};

/** Return shape for the care-time summary hook. @agentic connections */
export type UseCareTimeSummaryResult = {
  summary: CareTimeSummary | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * Hook: compute the CoCM eligibility + billing summary for one patient
 * in one calendar month.
 *
 * Auto-refreshes when a `CareTimeCreated`/`CareTimeUpdated`/
 * `CareTimeDeleted` event fires for this patient.
 * @agentic connections
 */
export const useCareTimeSummary = (
  patientId: string | undefined,
  options: UseCareTimeSummaryOptions = {},
): UseCareTimeSummaryResult => {
  const medplum = useMedplum();
  const [summary, setSummary] = useState<CareTimeSummary | undefined>(
    undefined,
  );
  // Match useCoCMBillingSummary: block BillingModal until the first eligibility
  // fetch settles so the wizard never mounts with a one-frame empty prefill.
  const [loading, setLoading] = useState<boolean>(() => Boolean(patientId));
  const [error, setError] = useState<Error | undefined>(undefined);
  const [version, setVersion] = useState(0);
  const monthKey =
    options.month instanceof Date
      ? options.month.toISOString().slice(0, 7)
      : new Date().toISOString().slice(0, 7);

  const monthBounds = useMemo(
    () => getCalendarMonthBounds(options.month ?? new Date()),
    [monthKey],
  );

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  // Key of the last patient-month we've settled a fetch for (success OR error).
  // A `version`-bump refetch (fired by the CareTime* domain events below, e.g.
  // after a billable toggle) refreshes in the background WITHOUT re-entering the
  // blocking `loading` state; only an initial load or a change of
  // patient/month does. BillingModal gates mounting its SteppedForm wizard on
  // this `loading` (like useCoCMBillingSummary), so a refetch that flipped it
  // back to `true` would unmount and remount the wizard, snapping the user back
  // to step 1.
  const loadedKeyRef = useRef<string | undefined>(undefined);
  const summaryCacheRef = useRef<Map<string, CareTimeSummary>>(new Map());

  useEffect(() => {
    if (!patientId) {
      return;
    }
    const queryKey = `${patientId}:${monthBounds.start}:${monthBounds.end}`;
    const isInitialLoad = loadedKeyRef.current !== queryKey;
    let cancelled = false;

    /* eslint-disable react-hooks/set-state-in-effect -- intentional sync gate */
    if (isInitialLoad) {
      setLoading(true);
      setSummary(
        summaryCacheRef.current.has(queryKey)
          ? summaryCacheRef.current.get(queryKey)
          : undefined,
      );
    } else if (loadedKeyRef.current === queryKey) {
      if (summaryCacheRef.current.has(queryKey)) {
        setSummary(summaryCacheRef.current.get(queryKey));
      }
      setLoading(false);
    }
    setError(undefined);
    /* eslint-enable react-hooks/set-state-in-effect */

    computeCoCMEligibility(medplum, {
      patientId,
      monthStart: monthBounds.start,
      monthEnd: monthBounds.end,
      patient: options.patient,
    })
      .then((result) => {
        if (cancelled) return;
        summaryCacheRef.current.set(queryKey, result);
        setSummary(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled) return;
        loadedKeyRef.current = queryKey;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    medplum,
    patientId,
    monthBounds.start,
    monthBounds.end,
    version,
    options.patient,
  ]);

  // Stable subscription identity under React 18 StrictMode — keep the
  // latest refetch closure on a ref so the deps array is patient-only.
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  useEffect(() => {
    if (!patientId) return undefined;
    const onEvent = (payload: EventWithPatient) => {
      if (payload.patientId === patientId) {
        refetchRef.current();
      }
    };
    const unsubCreated = globalEventEmitter.subscribe<CareTimeCreatedEvent>(
      DomainEventNames.CareTimeCreated,
      onEvent,
    );
    const unsubUpdated = globalEventEmitter.subscribe<CareTimeUpdatedEvent>(
      DomainEventNames.CareTimeUpdated,
      onEvent,
    );
    const unsubDeleted = globalEventEmitter.subscribe<CareTimeDeletedEvent>(
      DomainEventNames.CareTimeDeleted,
      onEvent,
    );
    return () => {
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
    };
  }, [patientId]);

  return {
    summary: patientId ? summary : undefined,
    loading: patientId ? loading : false,
    error: patientId ? error : undefined,
    refetch,
  };
};
